import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useSupport } from './SupportWidget';

interface Ticket {
  ticketId: string;
  ticketNum: string;
  subject: string;
  category: string;
  status: string;
  lastMessageAt: string;
  createdOn?: string;
}
interface Message {
  msgId: string;
  author: 'customer' | 'agent' | 'system';
  authorName?: string;
  body: string;
  createdOn: string;
}

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open', PENDING: 'Awaiting you', RESOLVED: 'Resolved', CLOSED: 'Closed',
};

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '');

export function MyTickets() {
  const { openSupport } = useSupport();
  const [selected, setSelected] = useState<string | null>(null);

  const tickets = useQuery({ queryKey: ['my-tickets'], queryFn: () => api.get<Ticket[]>('/support/tickets') });

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Support</h1>
          <div className="sub">Your support tickets and conversations with our team.</div>
        </div>
        <button className="btn btn-primary" onClick={openSupport}>+ New request</button>
      </div>

      {tickets.isLoading ? (
        <div className="loading-screen">// loading your tickets…</div>
      ) : (tickets.data?.length ?? 0) === 0 ? (
        <div className="panel empty">
          <h3>No tickets yet</h3>
          <p>Need a hand? <button className="linklike" onClick={openSupport}>Open a support request</button> or ask our assistant using the “Get help” button.</p>
        </div>
      ) : (
        <div className="admin-split">
          <div className="panel" style={{ padding: 0 }}>
            <ul className="ticket-list">
              {tickets.data?.map((t) => (
                <li key={t.ticketId}>
                  <button
                    className={`ticket-row${selected === t.ticketId ? ' is-active' : ''}`}
                    onClick={() => setSelected(t.ticketId)}
                  >
                    <div className="ticket-row-top">
                      <span className="ticket-subject">{t.subject}</span>
                      <span className={`status-pill s-${t.status.toLowerCase()}`}>{STATUS_LABEL[t.status] ?? t.status}</span>
                    </div>
                    <div className="ticket-row-sub">
                      <span className="mono">{t.ticketNum}</span> · updated {when(t.lastMessageAt)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="panel">
            {selected ? <TicketThread ticketId={selected} /> : <p className="sub">Select a ticket to view the conversation.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function TicketThread({ ticketId }: { ticketId: string }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const thread = useQuery({
    queryKey: ['my-ticket', ticketId],
    queryFn: () => api.get<{ ticket: Ticket; messages: Message[] }>(`/support/tickets/${ticketId}`),
  });

  const send = useMutation({
    mutationFn: () => api.post(`/support/tickets/${ticketId}/messages`, { message: reply.trim() }),
    onSuccess: () => {
      setReply(''); setErr(null);
      qc.invalidateQueries({ queryKey: ['my-ticket', ticketId] });
      qc.invalidateQueries({ queryKey: ['my-tickets'] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not send your reply'),
  });

  if (thread.isLoading) return <div className="loading-screen">// loading…</div>;
  const t = thread.data?.ticket;
  const closed = t?.status === 'CLOSED';

  return (
    <div className="ticket-thread">
      <div className="ticket-thread-head">
        <h3>{t?.subject}</h3>
        <div className="sub"><span className="mono">{t?.ticketNum}</span> · {STATUS_LABEL[t?.status ?? ''] ?? t?.status}</div>
      </div>
      <div className="ticket-messages">
        {thread.data?.messages.map((m) => (
          <div key={m.msgId} className={`ticket-msg from-${m.author}`}>
            <div className="ticket-msg-meta">
              {m.author === 'agent' ? 'Support' : m.author === 'system' ? 'Assistant' : 'You'} · {when(m.createdOn)}
            </div>
            <div className="ticket-msg-body">{m.body}</div>
          </div>
        ))}
      </div>
      {!closed && (
        <div className="ticket-reply">
          <textarea className="input" rows={3} value={reply} placeholder="Write a reply…"
            onChange={(e) => setReply(e.target.value)} />
          {err && <p className="cart-error">{err}</p>}
          <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }}
            disabled={send.isPending || reply.trim().length < 2} onClick={() => send.mutate()}>
            {send.isPending ? 'Sending…' : 'Send reply'}
          </button>
        </div>
      )}
    </div>
  );
}
